import { IGetOneResponse } from '../util/Types/Generic';
import { ApiService } from './ApiService';

interface SecDefaultTypes {
  query: string;
  from: number;
  size: number;
  flowId?: string;
}

interface SecFullTextSearchTypes {
  query: string;
  formTypes: string[];
  startDate: string;
  endDate: string;
  flowId?: string;
}

interface SecExtractorTypes {
  url: string;
  section: string;
  type: string;
  flowId?: string;
}

export type SecModel =
  | 'sec-query'
  | 'sec-full-text-search'
  | 'sec-extractor'
  | 'sec-trading'
  | 'sec-form-d'
  | 'sec-adv-firm-search'
  | 'sec-adv-individual-registered-adviser'
  | 'sec-adv-direct-owners'
  | 'sec-indirect-owners'
  | 'sec-adv-financial-industry-affiliations'
  | 'sec-adv-private-funds'
  | 'sec-adv-brochure-api'
  | 'sec-cik'
  | 'sec-ticker'
  | 'sec-cusip'
  | 'sec-company-name';

export interface SecQueryInput {
  model: SecModel;
  query: string;
  from?: number;
  size?: number;
  flowId?: string;
  formTypes?: string[];
  startDate?: string;
  endDate?: string;
  section?: string;
  type?: string;
}

export class SecService extends ApiService {
  static pathName = '/sec';

  static query = (body: SecDefaultTypes): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/query`, body);
  };

  static fullTextSearch = (
    body: SecFullTextSearchTypes,
  ): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/full-text-search`, body);
  };

  static extractor = (
    body: SecExtractorTypes,
  ): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/extractor`, body);
  };

  static trading = (body: SecDefaultTypes): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/trading`, body);
  };

  static formD = (body: SecDefaultTypes): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/form-d`, body);
  };

  static advFirmSearch = (
    body: SecDefaultTypes,
  ): Promise<IGetOneResponse<any>> => {
    return this.post(`${this.pathName}/adv-firm-search`, body);
  };

  static advIndividualRegisteredAdviser = (
    body: SecDefaultTypes,
  ): Promise<IGetOneResponse<any>> => {
    return this.post(
      `${this.pathName}/adv-individual-registered-adviser`,
      body,
    );
  };
  static advDirectOwners = (crd: string): Promise<IGetOneResponse<any>> => {
    return this.get(`${this.pathName}/adv-direct-owners?crd=${crd}`);
  };

  static advIndirectOwners = (crd: string): Promise<IGetOneResponse<any>> => {
    return this.get(`${this.pathName}/adv-indirect-owners?crd=${crd}`);
  };

  static advFinancialIndustryAffiliations = (
    crd: string,
  ): Promise<IGetOneResponse<any>> => {
    return this.get(
      `${this.pathName}/adv-financial-industry-affiliations?crd=${crd}`,
    );
  };

  static advPrivateFunds = (crd: string): Promise<IGetOneResponse<any>> => {
    return this.get(`${this.pathName}/adv-private-funds?crd=${crd}`);
  };

  static advBrochureApi = (crd: string): Promise<IGetOneResponse<any>> => {
    return this.get(`${this.pathName}/adv-brochure-api?crd=${crd}`);
  };

  static cik = (cik: string): Promise<IGetOneResponse<any>> => {
    return this.get(`${this.pathName}/cik?cik=${cik}`);
  };

  static ticker = (ticker: string): Promise<IGetOneResponse<any>> => {
    return this.get(`${this.pathName}/ticker?ticker=${ticker}`);
  };

  static cusip = (cusip: string): Promise<IGetOneResponse<any>> => {
    return this.get(`${this.pathName}/cusip?cusip=${cusip}`);
  };

  static companyName = (companyName: string): Promise<IGetOneResponse<any>> => {
    return this.get(`${this.pathName}/company-name?name=${companyName}`);
  };

  static dispatch = (
    input: SecQueryInput,
  ): Promise<IGetOneResponse<unknown>> => {
    const {
      model,
      query,
      from = 0,
      size = 10,
      flowId,
      formTypes,
      startDate,
      endDate,
      section,
      type,
    } = input;

    if (model === 'sec-query') return this.query({ query, from, size, flowId });
    if (model === 'sec-full-text-search')
      return this.fullTextSearch({
        query,
        formTypes: formTypes ?? [],
        startDate: startDate ?? '',
        endDate: endDate ?? '',
        flowId,
      });
    if (model === 'sec-extractor')
      return this.extractor({
        url: query,
        section: section ?? '',
        type: type ?? '',
        flowId,
      });
    if (model === 'sec-trading')
      return this.trading({ query, from, size, flowId });
    if (model === 'sec-form-d')
      return this.formD({ query, from, size, flowId });
    if (model === 'sec-adv-firm-search')
      return this.advFirmSearch({ query, from, size, flowId });
    if (model === 'sec-adv-individual-registered-adviser')
      return this.advIndividualRegisteredAdviser({ query, from, size, flowId });
    if (model === 'sec-adv-direct-owners') return this.advDirectOwners(query);
    if (model === 'sec-indirect-owners') return this.advIndirectOwners(query);
    if (model === 'sec-adv-financial-industry-affiliations')
      return this.advFinancialIndustryAffiliations(query);
    if (model === 'sec-adv-private-funds') return this.advPrivateFunds(query);
    if (model === 'sec-adv-brochure-api') return this.advBrochureApi(query);
    if (model === 'sec-cik') return this.cik(query);
    if (model === 'sec-ticker') return this.ticker(query);
    if (model === 'sec-cusip') return this.cusip(query);
    return this.companyName(query);
  };
}
